// react
import { useContext, useState } from 'react';

// types
import { Transaction, Utilization, UtilizationTransaction } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// ui
import { Accordion, AccordionProps } from '../../ui/Accordion/Accordion';
import { TableProps } from '../../ui/Table/Table';

// tables
import { TableBasicTransactions } from '../../tables/basic/TableBasicTransactions/TableBasicTransactions';
import { TableBasicUtilizationCurrencies } from '../../tables/basic/TableBasicUtilizationCurrencies/TableBasicUtilizationCurrencies';

// cards
import { CardSeverity, CardSeverityProps } from '../../cards/CardSeverity/CardSeverity';

// utils
import { formatNumber, formatPercentage } from '../../../utils/format';

// props
export interface DialogUtilizationProps extends DialogActionsProps {
	isOpen: boolean;
	onClose: () => void;
	type?: 'ENTITY' | 'LOAN' | 'PROJECT';
	utilization?: Utilization;
}

export const DialogUtilization = ({
	isOpen,
	onClose,
	type = 'ENTITY',
	utilization,
	...props
}: DialogUtilizationProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);

	// state
	const [transactionsExcluded, setTransactionsExcluded] = useState<Transaction[]>([]);

	// vars
	const idsTransactionsExcluded =
		utilization?.in?.excluded?.concat(utilization?.out?.excluded) || [];
	const utilizationTransactionsExcluded: UtilizationTransaction[] = [];

	// gather excluded transactions
	utilization?.currencies?.forEach((currency) => {
		const transactionsExcludedForCurrency = [
			...currency.in.transactions,
			...currency.out.transactions,
		].filter((t) => idsTransactionsExcluded?.includes(t.id));

		utilizationTransactionsExcluded.push(...transactionsExcludedForCurrency);
	});

	// sort highest excluded transactions
	utilizationTransactionsExcluded.sort((a, b) => b.amount - a.amount);

	// fetch excluded transactions
	useFetch({
		isEnabled: Boolean(idsTransactionsExcluded?.length),
		url: `${process.env.REACT_APP_API_URL}/transactions?id=${utilizationTransactionsExcluded
			?.slice(0, 3)
			?.map((t) => t.id)
			.join(',')}&populate=fromAccount,toAccount`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransactionsExcluded(
					res.data.sort((a: Transaction, b: Transaction) => b.total - a.total)
				);
			}
		},
	});

	const data = utilization?.currencies as TableProps['data'];
	const currencyReporting = project?.currencies?.find((c) => c.type === 'REPORTING');
	const itemsAccordion: AccordionProps['items'] = [
		{
			AccordionDetailsProps: {
				children: t('dialogUtilization.entity.content'),
			},
			AccordionProps: {
				defaultExpanded: type === 'ENTITY',
			},
			AccordionSummaryProps: {
				children: t('dialogUtilization.entity.title'),
				IconCircleProps: {
					type: 'entity',
				},
			},
		},
		{
			AccordionDetailsProps: {
				children: t('dialogUtilization.loan.content'),
			},
			AccordionProps: {
				defaultExpanded: type === 'LOAN',
			},
			AccordionSummaryProps: {
				children: t('dialogUtilization.loan.title'),
				IconCircleProps: {
					type: 'loan',
				},
			},
		},
		{
			AccordionDetailsProps: {
				children: t('dialogUtilization.project.content'),
			},
			AccordionProps: {
				defaultExpanded: type === 'PROJECT',
			},
			AccordionSummaryProps: {
				children: t('dialogUtilization.project.title'),
				IconCircleProps: {
					type: 'project',
				},
			},
		},
	];

	const CardSeverityUtilizationProps: Partial<CardSeverityProps> = {
		ChipStatusProps: {
			status: 'PENDING',
		},
		footnote: t('notEnoughInformation'),
		severity: 'default' as const,
	};

	if (utilization && typeof utilization.value === 'number') {
		if (utilization.value <= 30) {
			CardSeverityUtilizationProps.ChipStatusProps = {
				status: 'LOW',
			};
			CardSeverityUtilizationProps.severity = 'error';
		} else if (utilization.value <= 60) {
			CardSeverityUtilizationProps.ChipStatusProps = {
				status: 'MINIMAL',
			};
			CardSeverityUtilizationProps.severity = 'warning';
		} else if (utilization.value <= 70) {
			CardSeverityUtilizationProps.ChipStatusProps = {
				status: 'MODERATE',
			};
			CardSeverityUtilizationProps.severity = 'info';
		} else {
			CardSeverityUtilizationProps.ChipStatusProps = {
				status: 'HIGH',
			};
			CardSeverityUtilizationProps.severity = 'success';
		}

		CardSeverityUtilizationProps.footnote = t('xofYExpensed', {
			disbursed: formatNumber(utilization?.in?.total || 0, {
				notation: 'compact',
			}),
			expensed: formatNumber(utilization?.out?.total || 0, {
				notation: 'compact',
			}),
		});
	}

	const CardSeverityCoverageProps: Partial<CardSeverityProps> = {
		ChipStatusProps: {
			status: 'PENDING',
		},
		footnote: t('notEnoughInformation'),
		severity: 'default' as const,
	};

	if (utilization && typeof utilization.coverage === 'number') {
		if (utilization.coverage >= 80) {
			CardSeverityCoverageProps.ChipStatusProps = {
				status: 'HIGH',
			};
			CardSeverityCoverageProps.severity = 'success';
		} else if (utilization.coverage >= 60) {
			CardSeverityCoverageProps.ChipStatusProps = {
				status: 'MODERATE',
			};
			CardSeverityCoverageProps.severity = 'info';
		} else if (utilization.coverage >= 50) {
			CardSeverityCoverageProps.ChipStatusProps = {
				status: 'MINIMAL',
			};
			CardSeverityCoverageProps.severity = 'warning';
		} else {
			CardSeverityCoverageProps.ChipStatusProps = {
				status: 'LOW',
			};
			CardSeverityCoverageProps.severity = 'error';
		}

		CardSeverityCoverageProps.footnote = t('xOfYtransactions', {
			included: utilization.in.included.length + utilization.out.included.length,
			total:
				utilization.in.included.length +
				utilization.in.excluded.length +
				utilization.out.included.length +
				utilization.out.excluded.length,
		});
	}

	let currencyCode = '';

	if (currencyReporting) {
		currencyCode = `${currencyReporting.currency} `;
	}

	let alertExcludedTransactions = '';
	if (utilizationTransactionsExcluded.length === 1) {
		alertExcludedTransactions = t('alert.excludedTransactionsSingle', {
			currency: currencyReporting?.currency,
		});
	} else if (utilizationTransactionsExcluded.length > 1) {
		alertExcludedTransactions = t('alert.excludedTransactions', {
			total: utilizationTransactionsExcluded.length,
			currency: currencyReporting?.currency,
		});
	}

	return (
		<DialogActions
			actions={[
				{
					children: t('done'),
					onClick: onClose,
					variant: 'contained',
				},
			]}
			hasClose={true}
			isOpen={isOpen}
			maxWidth={700}
			onClose={onClose}
			title={{
				children: t('fundsUtilized'),
				variant: 'h3',
			}}
			{...props}>
			<Grid container spacing={2}>
				{utilization && (
					<>
						<Grid item xs={12}>
							<Grid container spacing={2}>
								<Grid item xs={12} md={6}>
									<CardSeverity
										ChipStatusProps={CardSeverityUtilizationProps.ChipStatusProps}
										footnote={CardSeverityUtilizationProps.footnote}
										hasBoxShadow={false}
										label={{
											children: `${currencyCode}${t('utilization')}`,
										}}
										severity={CardSeverityUtilizationProps.severity || 'default'}
										spacing={2}
										value={{
											children:
												typeof utilization.value === 'number'
													? formatPercentage(utilization.value)
													: t('na'),
											component: 'div',
											variant: 'h1',
										}}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<CardSeverity
										ChipStatusProps={CardSeverityCoverageProps.ChipStatusProps}
										footnote={CardSeverityCoverageProps.footnote}
										hasBoxShadow={false}
										label={{ children: `${currencyCode}${t('coverage')}` }}
										severity={CardSeverityCoverageProps.severity || 'default'}
										spacing={2}
										value={{
											children:
												typeof utilization.coverage === 'number'
													? formatPercentage(utilization.coverage)
													: t('na'),
											component: 'div',
											variant: 'h1',
										}}
									/>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Typography fontWeight={600}>{t('currencyBreakdown')}</Typography>
						</Grid>
						<Grid item xs={12}>
							<TableBasicUtilizationCurrencies
								data={data}
								utilization={utilization as Utilization}
							/>
						</Grid>
					</>
				)}
				{transactionsExcluded.length > 0 && (
					<>
						<Grid item xs={12}>
							<Typography fontWeight={600}>{t('excludedTransactions')}</Typography>
						</Grid>
						<Grid item xs={12}>
							<Alert severity="warning">{alertExcludedTransactions}</Alert>
						</Grid>
						<Grid item xs={12}>
							<TableBasicTransactions
								borderRadius={2}
								color="grey"
								data={transactionsExcluded}
								hasBorder={true}
								headersIncluded={['fromAccount', 'recorded', 'toAccount', 'total', 'type']}
							/>
						</Grid>
						<Grid item xs={12}>
							<Typography color="brand.grey.500" variant="caption">
								These transactions are considered high priority and should be updated.
							</Typography>
						</Grid>
					</>
				)}
				<Grid item xs={12}>
					<Typography fontWeight={600}>{t('dialogUtilization.what.title')}</Typography>
					<Typography>{t('dialogUtilization.what.content')}</Typography>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="info">
						<AlertTitle>{t('important')}</AlertTitle>
						{t('alert.utilization')}
					</Alert>
				</Grid>
				<Grid item xs={12}>
					<Typography fontWeight={600}>{t('dialogUtilization.how.title')}</Typography>
				</Grid>
				<Grid item xs={12}>
					<Accordion items={itemsAccordion} />
				</Grid>
			</Grid>
		</DialogActions>
	);
};
